import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { FaArrowLeft, FaBrain } from "react-icons/fa";
import { API_BASE_URL } from "../../config/apiConfig";
import { formatAiText } from "../../utils/formatAiText";

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const StudyPlan = () => {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const studentId = params.get("studentId");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchPlan = async () => {
            try {
                const url = new URL(`${API_BASE_URL}/api/study-plan`);
                if (studentId) url.searchParams.set("studentId", studentId);
                const res = await fetch(url, { headers: authHeaders() });
                const json = await res.json();
                if (!res.ok) throw new Error(json.message || "Failed to load study plan");
                setData(json.data);
            } catch (err) {
                toast.error(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchPlan();
    }, [studentId]);

    return (
        <div className="min-h-screen bg-gray-50">
            <nav className="bg-[#2148C0] shadow-md px-6 py-4 flex items-center gap-4">
                <button onClick={() => navigate(-1)} className="text-white"><FaArrowLeft size={18} /></button>
                <h1 className="text-xl font-bold text-white">Study Plan</h1>
            </nav>

            <div className="max-w-3xl mx-auto p-6 space-y-6">
                {loading && <p className="text-gray-500">Loading...</p>}

                {!loading && data && data.weakTopics.length === 0 && (
                    <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500">
                        Not enough practice history yet — take a few quizzes and your personalised plan will appear here.
                    </div>
                )}

                {!loading && data?.narrative && (
                    <div className="bg-blue-50 rounded-xl p-6 flex gap-3">
                        <FaBrain className="text-blue600 mt-1 flex-shrink-0" />
                        <p className="text-blue-900 text-sm whitespace-pre-wrap">{formatAiText(data.narrative)}</p>
                    </div>
                )}

                {!loading && data?.weakTopics?.length > 0 && (
                    <div>
                        <h3 className="font-semibold text-gray-800 mb-3">Focus on these topics first</h3>
                        <div className="space-y-2">
                            {data.weakTopics.map((t, i) => (
                                <div key={i} className="bg-white rounded-xl shadow-sm p-4 flex items-center justify-between">
                                    <div>
                                        <p className="font-medium text-gray-800">{t.topic}</p>
                                        <p className="text-sm text-gray-500">{t.subject} · {t.attempts} attempts</p>
                                    </div>
                                    <span className={`text-sm font-bold ${t.accuracy < 50 ? "text-red-500" : "text-yellow-600"}`}>{t.accuracy}%</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {!loading && data?.recommendedLessons?.length > 0 && (
                    <div>
                        <h3 className="font-semibold text-gray-800 mb-3">Recommended lessons</h3>
                        <div className="space-y-2">
                            {data.recommendedLessons.map((lesson) => (
                                <button key={lesson.id} onClick={() => navigate(`/lessons/${lesson.id}`)}
                                    className="w-full text-left bg-white rounded-xl shadow-sm p-4 hover:shadow-md transition">
                                    <p className="font-medium text-gray-800">{lesson.title}</p>
                                    <p className="text-sm text-gray-500">{lesson.subject} · {lesson.topic}</p>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StudyPlan;
